const containers = new Map();

const createContainer = (el, context) => {
    const container = document.createElement('div');
    container.className = `slds-scope ${context.$el.className}`;
    container.appendChild(el);
    document.body.appendChild(container);
    context.$emit('detached', container);
    return container;
};

const alignToAnchor = (el, vnode, { anchorRef, resizeToAnchor }) => {
    // first set the element to fixed so it does not affect the dimensions/flow of the UI
    el.style.position = 'fixed';

    const anchorRect = anchorRef.getBoundingClientRect();
    el.style.top = `${anchorRect.top + anchorRef.offsetHeight}px`;
    el.style.left = `${anchorRect.left}px`;

    if (resizeToAnchor) {
        el.style.width = `${anchorRef.offsetWidth}px`;
    }
};

const getScrollbarWidth = () => {
    const scrollDiv = document.createElement('div');
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.top = '-9999px';
    document.body.appendChild(scrollDiv);

    // Get the scrollbar width
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    scrollDiv.remove();
    return scrollbarWidth;
};

const adjustForViewPortOverflow = (el, { anchorRef, anchorTopOnVerticalOverflow }) => {
    const dropdownRect = el.getBoundingClientRect();
    const documentClientHeight = document.documentElement.clientHeight;
    const documentClientWidth = document.documentElement.clientWidth;
    const windowDocumentVerticalDiff = documentClientHeight - window.innerHeight;

    let viewportHeight;
    if (windowDocumentVerticalDiff <= 0) {
        viewportHeight = documentClientHeight;
    } else {
        viewportHeight = window.innerHeight - getScrollbarWidth();
    }

    // overflow off left side of viewport
    if (dropdownRect.x < 0) {
        el.style.left = `${el.offsetLeft + Math.abs(dropdownRect.x)}px`;
    // overflow off right side of viewport
    } else if (dropdownRect.left + dropdownRect.width > documentClientWidth) {
        const overflowXDiff = (dropdownRect.left + dropdownRect.width) - documentClientWidth - window.pageXOffset;
        el.style.left = `${dropdownRect.left - overflowXDiff}px`;
    }

    const dropdownHeight = el.scrollHeight;
    let adjustedVerticalPosition;

    if (anchorRef && anchorTopOnVerticalOverflow) {
        const anchor = anchorRef.getBoundingClientRect();
        let anchorY = anchor.bottom;

        if (anchorY + dropdownHeight > viewportHeight) {
            anchorY = anchor.top - dropdownHeight;
        }

        adjustedVerticalPosition = anchorY;
    } else if (dropdownRect.top + dropdownHeight > viewportHeight) {
        adjustedVerticalPosition = dropdownRect.top - (((dropdownRect.top + dropdownHeight)) - viewportHeight);
    }

    if (adjustedVerticalPosition) {
        el.style.top = `${adjustedVerticalPosition}px`;
    }
};

const removeContainer = (el) => {
    let container = containers.get(el);
    if (!container) return;

    container.remove();
    container = null;
    containers.delete(el);
};

let scrollHandlerFn;

const scrollHandler = (el, _scrollHandler, e) => {
    if (e && e.type === 'scroll' && el && el.contains(e.target)) return;

    if (typeof _scrollHandler === 'function') {
        _scrollHandler(el, e);
    } else {
        removeContainer(el);
        window.removeEventListener('scroll', scrollHandlerFn, true);
        scrollHandlerFn = null;
    }
};

export default {
    bind(el, binding, vnode) {
        if (binding.value.disabled) return;

        vnode.context.$nextTick(() => {
            const options = binding.value;

            const container = createContainer(el, vnode.context);
            vnode.container = container;
            containers.set(el, container);

            if (typeof options.customDisplayMethod === 'function') {
                options.customDisplayMethod();
            }

            if (options.anchorRef && options.anchorRef instanceof HTMLElement) {
                alignToAnchor(el, vnode, options);
            }

            if (options.viewPortOverflow) {
                vnode.context.$nextTick(() => {
                    if (typeof options.onRender === 'function') {
                        options.onRender();
                        vnode.context.$nextTick(adjustForViewPortOverflow.bind(this, el, options));
                    } else {
                        adjustForViewPortOverflow(el, options);
                    }
                });
            }

            scrollHandlerFn = scrollHandler.bind(null, el, options.scrollHandler);
            window.addEventListener('scroll', scrollHandlerFn, true);
        });
    },
    update(el, { value: options }, vnode) {
        if (options.viewPortOverflow) {
            vnode.context.$nextTick(() => {
                if (typeof options.onRender === 'function') {
                    options.onRender();
                }

                adjustForViewPortOverflow(el, options);
            });
        }
    },
    unbind(el) {
        removeContainer(el);
        window.removeEventListener('scroll', scrollHandlerFn, true);
        scrollHandlerFn = null;
    }
};
